import DashboardCard, { DashboardCardProps } from '../DashboardCard/DashboardCard';
import './DashboardCardList.scss';
import { useEffect, useMemo, useState } from 'react';
import { DashboardCardCountType, DashboardCardType } from '../Types';
import { IconKSAP, IconMention, IconNbAsk, IconSmartStar } from 'components/Icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueueContext } from '../../Queue/QueueContext';
import { useQueryState } from 'hooks/useQueryState';
import { useAuth, useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';

type DashboardCardListProps = {
  onChange?: (selectedCard: 'Opportunities' | 'Requests' | 'Mentions') => void;
  selected: 'Opportunities' | 'Requests' | 'Mentions';
};

const DashboardCardList = ({ onChange, selected }: DashboardCardListProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = useAuth();
  const { currentLocationAbility } = useAuthContext();
  const { state, dispatch } = useQueueContext();
  const [filterMentions, setFilterMentions] = useQueryState('filterMentions');
  const { requests, filters } = state;
  const { locationId } = useParams();
  const { dashmode } = useParams();
  const payablesEnabled = false; // TODO: replace with actual logic to check if payables are enabled
  const [selectedCard, setSelectedCard] = useState<'Opportunities' | 'Requests' | 'Mentions'>(
    dashmode === 'Requests' ? 'Requests' : selected
  );
  const hasAccessQueue = currentLocationAbility?.can(HoneAbilityActions.Read, HoneAbilitySubjects.Queue);

  useEffect(() => {
    if (filterMentions === 'true') {
      setSelectedCard('Mentions');
    } else {
      setSelectedCard('Requests');
    }
  }, [filterMentions]);

  const requestsOpen = useMemo(() => {
    if (!requests) return 0;
    return requests.filter(request => request.status === 'Open').length;
  }, [requests]);

  const requestsInprogress = useMemo(() => {
    if (!requests) return 0;
    return requests.filter(request => request.status === 'In Progress').length;
  }, [requests]);

  const requestBlockers = useMemo(() => {
    if (!requests || !userId) return 0;
    // Only count blockers in "For Me" (Open status)
    return requests.filter(req => req.priority === 'Blocker' && req.status === 'Open').length;
  }, [requests, userId]);

  const mentionedRequestForClientsOrBookkeeper = useMemo(() => {
    if (!requests || !userId) return [];
    const tempRequests = [...requests];
    return tempRequests.filter(
      req => req.status === 'In Progress' && req.comments?.some(comment => comment.taggedUserIds?.includes(userId))
    );
  }, [requests, userId]);

  const mentionedRequestForMe = useMemo(() => {
    if (!requests || !userId) return [];
    const tempRequests = [...requests];
    return tempRequests.filter(
      req => req.status === 'Open' && req.comments?.some(comment => comment.taggedUserIds?.includes(userId))
    );
  }, [requests, userId]);

  const mentionedRequestBlocker = useMemo(() => {
    if (!requests || !userId) return [];
    const tempRequests = [...requests];
    return tempRequests.filter(
      req =>
        req.priority === 'Blocker' &&
        req.status === 'Open' &&
        req.comments?.some(comment => comment.taggedUserIds?.includes(userId))
    );
  }, [requests, userId]);

  useEffect(() => {
    if (onChange && (selectedCard === 'Opportunities' || selectedCard === 'Requests')) {
      onChange(selectedCard);
    }
  }, [selectedCard]);

  const handleBubbleClick = (
    cardType: 'Requests' | 'Mentions',
    status: 'Open' | 'In Progress',
    options: {
      priority?: string;
      mentions?: boolean;
    } = {}
  ) => {
    // Update selected card and mentions filter
    setSelectedCard(cardType);
    setFilterMentions(options.mentions ? 'true' : 'false');

    // Update queue filters
    dispatch({
      type: 'SET_FILTERS',
      payload: { ...filters, status, sort: 'oldest' },
    });

    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.set('status', status);
    newParams.set('filterMentions', options.mentions ? 'true' : 'false');

    if (options.priority) {
      newParams.set('priority', options.priority);
    } else {
      newParams.delete('priority');
    }

    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const dashboardCards: DashboardCardProps[] = [
    {
      type: 'normal' as DashboardCardType,
      icon: (<IconNbAsk />) as React.ReactNode,
      title: 'Requests' as string,
      badgesContainer: [
        {
          type: 'alert' as DashboardCardCountType,
          value: (requestBlockers || 0) as number,
          tooltip: 'Blockers in For Me',
          onClick: () => handleBubbleClick('Requests', 'Open', { priority: 'Blocker', mentions: false }),
        },
        {
          type: 'highlight' as DashboardCardCountType,
          value: (requestsOpen || 0) as number,
          tooltip: 'Requests For Me',
          onClick: () => handleBubbleClick('Requests', 'Open', { mentions: false }),
        },
        {
          type: 'normal' as DashboardCardCountType,
          value: (requestsInprogress || 0) as number,
          tooltip: hasAccessQueue ? 'Requests For Client' : 'Requests For KS',
          onClick: () => handleBubbleClick('Requests', 'In Progress', { mentions: false }),
        },
      ],
      onClick: () => {
        setSelectedCard('Requests');
        setFilterMentions('false');
      },
    },
    {
      type: 'highlight' as DashboardCardType,
      icon: (<IconMention />) as React.ReactNode,
      title: 'Mentions' as string,
      badgesContainer: [
        {
          type: 'alert' as DashboardCardCountType,
          value: (mentionedRequestBlocker.length || 0) as number,
          tooltip: 'Blockers in For Me with mentions',
          onClick: () => handleBubbleClick('Mentions', 'Open', { priority: 'Blocker', mentions: true }),
        },
        {
          type: 'highlight' as DashboardCardCountType,
          value: (mentionedRequestForMe.length || 0) as number,
          tooltip: 'For Me with mentions',
          onClick: () => handleBubbleClick('Mentions', 'Open', { mentions: true }),
        },
        {
          type: 'highlight' as DashboardCardCountType,
          value: (mentionedRequestForClientsOrBookkeeper.length || 0) as number,
          tooltip: hasAccessQueue ? 'For Client with mentions' : 'For KS with mentions',
          onClick: () => handleBubbleClick('Mentions', 'In Progress', { mentions: true }),
        },
      ],
      onClick: () => {
        setSelectedCard('Mentions');
        setFilterMentions('true');
      },
    },
    {
      type: 'highlight' as DashboardCardType,
      icon: (<IconSmartStar />) as React.ReactNode,
      title: 'Opportunities' as string,
      badgesContainer: 'Coming Soon' as string,
    },

    {
      type: 'highlight' as DashboardCardType,
      icon: (<IconKSAP />) as React.ReactNode,
      title: 'Payables' as string,
      badgesContainer: [
        {
          type: 'highlight' as DashboardCardCountType,
          value: 1 as number,
          tooltip: 'Invoice(s) due in 7 days',
        },
        {
          type: 'normal' as DashboardCardCountType,
          value: 2 as number,
        },
      ],
      onClick: () => {
        navigate(`/app/location/${locationId}/report/ap-aging`);
      },
    },
  ];

  return (
    <div className="dashboard-cards-lists-container">
      {dashboardCards.map((card, index) => {
        return (
          <div key={index} className="dashboard-cards-lists-container__card">
            {card.title === 'Payables' && !payablesEnabled ? null : (
              <DashboardCard {...card} selected={selectedCard === card.title} />
            )}
          </div>
        );
      })}
    </div>
  );
};
export default DashboardCardList;
